import { Icon } from '@iconify/react';
import { styled } from '@linaria/react';
import { Tooltip } from '@mantine/core';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { getQuickLinkForArtifact } from '../../../helpers/flora-quick-links/flora-quick-links';
import { Text } from '../../../ui-library/typography/typography';
import type { ArtifactSpec } from '../../flora/flora.type';
import { getArtifactEntry } from '../artifacts/artifact-registry';

interface AgentArtifactCardProps {
  artifact: ArtifactSpec;
}

export function AgentArtifactCard({ artifact }: AgentArtifactCardProps) {
  const [collapsed, setCollapsed] = useState(false);
  const entry = getArtifactEntry(artifact.type);

  const initialQuickLinks = useMemo(
    () => getQuickLinkForArtifact(artifact.type, artifact.data, artifact.config),
    [artifact.type, artifact.data, artifact.config],
  );

  const [quickLinks, setQuickLinks] = useState(initialQuickLinks);

  useEffect(() => {
    setQuickLinks(initialQuickLinks);
  }, [initialQuickLinks]);

  // Keyed links map to individual groups inside the artifact (e.g. one per board).
  // When there are multiple keyed links they render inline next to each group header.
  // A single keyed link (or any un-keyed link) shows in the card header instead.
  const keyedLinks = useMemo(() => quickLinks.filter((link) => !!link.key), [quickLinks]);
  const hasMultipleKeyedLinks = keyedLinks.length > 1;
  const headerLinks = useMemo(
    () => (hasMultipleKeyedLinks ? quickLinks.filter((link) => !link.key) : quickLinks),
    [quickLinks, hasMultipleKeyedLinks],
  );
  const inlineLinks = useMemo(() => (hasMultipleKeyedLinks ? keyedLinks : []), [keyedLinks, hasMultipleKeyedLinks]);

  const handleQuickLinkClick = useCallback((e: React.MouseEvent, url: string) => {
    e.stopPropagation();
    window.open(url, '_blank', 'noopener,noreferrer');
  }, []);

  if (!entry) {
    return (
      <Card id={`artifact-${artifact.id}`}>
        <CardHeader onClick={() => setCollapsed(!collapsed)} role="button" tabIndex={0}>
          <HeaderLeft>
            <ChevronIcon data-collapsed={collapsed}>
              <Icon icon="material-symbols:chevron-right" width={16} height={16} />
            </ChevronIcon>
            <CardTitleText size="small" weight="bold" color="var(--ink-darkest)">
              {artifact.title}
            </CardTitleText>
          </HeaderLeft>
        </CardHeader>
        {!collapsed && (
          <FallbackBody>
            <Text size="small" color="var(--ink-lighter)">
              Unsupported artifact type: {artifact.type}
            </Text>
          </FallbackBody>
        )}
      </Card>
    );
  }

  const Component = entry.component;

  return (
    <Card id={`artifact-${artifact.id}`}>
      <CardHeader onClick={() => setCollapsed(!collapsed)} role="button" tabIndex={0}>
        <HeaderLeft>
          <ChevronIcon data-collapsed={collapsed}>
            <Icon icon="material-symbols:chevron-right" width={16} height={16} />
          </ChevronIcon>
          <ArtifactIcon>
            <Icon icon="material-symbols:bar-chart-4-bars-rounded" width={16} height={16} />
          </ArtifactIcon>
          <CardTitleText size="small" weight="bold" color="var(--ink-darkest)">
            {artifact.title}
          </CardTitleText>
        </HeaderLeft>
        {headerLinks.length > 0 && (
          <HeaderRight>
            {headerLinks.map((link) => (
              <Tooltip key={link.url} label={link.label} position="top" withArrow>
                <QuickLinkButton onClick={(e) => handleQuickLinkClick(e, link.url)} aria-label={link.label}>
                  <Icon icon="material-symbols:open-in-new" width={16} height={16} />
                </QuickLinkButton>
              </Tooltip>
            ))}
          </HeaderRight>
        )}
      </CardHeader>
      {!collapsed && (
        <CardBody>
          <Component
            data={artifact.data}
            config={artifact.config}
            quickLinks={inlineLinks}
            onQuickLinksChange={setQuickLinks}
          />
        </CardBody>
      )}
    </Card>
  );
}

const Card = styled.div`
  background: var(--base-white);
  border: 1px solid var(--sky-light);
  border-radius: 10px;
  overflow: hidden;
  flex-shrink: 0;
`;

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 14px;
  border-bottom: 1px solid var(--sky-light);
  color: var(--ink-dark);
  cursor: pointer;
  user-select: none;
  transition: background 0.15s;

  &:hover {
    background: var(--sky-lightest);
  }
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
`;

const ChevronIcon = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--ink-lighter);
  transition: transform 0.2s ease;
  transform: rotate(90deg);

  &[data-collapsed='true'] {
    transform: rotate(0deg);
  }
`;

const ArtifactIcon = styled.span`
  display: flex;
  align-items: center;
  color: var(--ink-lighter);
`;

const CardTitleText = styled(Text)`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const CardBody = styled.div`
  padding: 14px;
  min-height: 200px;
`;

const FallbackBody = styled.div`
  padding: 20px 14px;
  text-align: center;
`;

const HeaderRight = styled.div`
  display: flex;
  align-items: center;
  margin-left: auto;
  flex-shrink: 0;
`;

const QuickLinkButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  padding: 0;
  border: none;
  border-radius: 6px;
  background: transparent;
  color: var(--ink-lighter);
  cursor: pointer;
  transition: background 0.15s, color 0.15s;

  &:hover {
    background: var(--sky-light);
    color: var(--secondary-base);
  }
`;
